/* eslint-disable indent */
import useRealtimeChannel from "../hooks/useRealtimeChannel";
import ListingActions, { ListingSelectors } from "../reducers/ListingRedux";
import AuditActions, { AuditSelectors } from "../reducers/AuditRedux";
import React, { createContext, useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { BIDDER_STATUS } from "../themes/cashrent/common";
import { DateTime } from "luxon";

const initialState = {
  listing: undefined,
  bidders: [],
};

const actions = {
  setListingId: () => {},
  canBid: () => {},
};

export const RealTimeBidsContext = createContext({ ...initialState, ...actions });

const RealTimeBidsContextProvider = ({ children }) => {
    const listing = useSelector(ListingSelectors.listingData);
    const bidderList = useSelector(AuditSelectors.bidderList);
    const [channel, setChannel] = useState();
    const [bidders] = useState([]);
    const dispatch = useDispatch();
    const { subscribe, unsubscribe } = useRealtimeChannel();

    useEffect(() => {
       if (listing?.id) {
        if (channel) {
          unsubscribe(channel, channel.name);
        }
        const registeredChannel = subscribe(`bids-${listing.id}`, ["bid::updated", "listing::updated", "bidder::updated"], () => {
            refreshBids();
        });
        setChannel(registeredChannel);
      }

      return () => channel && listing?.id && unsubscribe(channel, `bids-${listing.id}`);
    }, [listing.id]);

    useEffect(() => {
      if (listing.isSaleNonAuction) return;

      const nowWithZone = DateTime.local().setZone(listing.timeZone);
      const auctionStarts = DateTime.fromISO(listing.start_date, { zone: listing.timeZone });
      const auctionEnds = DateTime.fromISO(listing.bidding_end, { zone: listing.timeZone });
      const auctionWillStartSoon = nowWithZone < auctionStarts && nowWithZone.plus({ minutes: 30 }) > auctionStarts;
      const auctionWillEndSoon = nowWithZone < auctionEnds && nowWithZone.plus({ minutes: 30 }) > auctionEnds;

      if (auctionWillStartSoon || auctionWillEndSoon) {
        const ms = auctionWillStartSoon
          ? auctionStarts.diff(nowWithZone, "milliseconds").toObject().milliseconds
          : auctionEnds.diff(nowWithZone, "milliseconds").toObject().milliseconds;

        const refreshTimeout = setTimeout(() => {
          refreshBids();
        }, ms);

        return () => clearTimeout(refreshTimeout);
      }
    }, [listing.isSaleNonAuction, listing.start_date, listing.bidding_end]);

    const refreshBids = () => {
      dispatch(ListingActions.fetchListingDetails(listing.id));
      dispatch(AuditActions.fetchAuditData({ action: { data: {} } }));
    };

    const setListingId = (listingId) => {
      if (listingId !== listing?.id) {
          dispatch(ListingActions.fetchListingDetails(listingId));
      }
    };

    const canBid = useCallback(() => !listing?.manager || bidderList[BIDDER_STATUS.REGISTERED].includes(listing.id), [listing, bidderList]);

    return (
      <RealTimeBidsContext.Provider
        value={{ setListingId, listing, bidders, canBid }}
      >
        {children}
      </RealTimeBidsContext.Provider>
    );
};

export default RealTimeBidsContextProvider;
